import { useCallback, useEffect, useRef, useState } from "react";
import {
  trackConversationEnded,
  trackConversationStarted,
  trackError,
} from "./analytics";
import { WidgetApiClient } from "./api-client";
import { useHedraAvatarManager } from "./hooks/useHedraAvatarManager";
import { useWidgetState } from "./hooks/useWidgetState";
import type { WidgetConfig } from "./types";

type HedraAvatarWidgetProps = Required<WidgetConfig>;

/**
 * Hedra Avatar Widget - React Component
 *
 * A floating avatar widget with voice interaction capabilities.
 * Uses LiveKit for real-time audio/video streaming.
 *
 * New architecture:
 * - User microphone audio is published to LiveKit room
 * - Python agent handles VAD, STT, LLM, and TTS
 * - Hedra avatar receives TTS audio and renders lip-synced video
 * - Widget just displays video and shows connection state
 */
export function HedraAvatarWidget(props: HedraAvatarWidgetProps) {
  const {
    apiBaseUrl,
    widgetId,
    avatarId,
    voiceId,
    placeholderImage,
    width,
    height,
    position,
  } = props;

  const { state, setState } = useWidgetState();
  const [countdown, setCountdown] = useState<number | null>(null);

  const videoRef = useRef<HTMLVideoElement>(null);
  const apiClientRef = useRef<WidgetApiClient>(
    new WidgetApiClient(apiBaseUrl, widgetId)
  );
  const inactivityTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const countdownStartTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const conversationStartTime = useRef<number | null>(null);

  const avatarManager = useHedraAvatarManager({
    videoRef,
    onStateChange: setState,
    onStreamReady: () => {
      // Avatar video is ready, start listening
      setState("listening");
    },
    onAgentSpeaking: () => {
      setState("speaking");
    },
    onAgentListening: () => {
      setState("listening");
    },
    onError: (error) => {
      console.error("Avatar error:", error);
      setState("error");
    },
    onDisconnected: () => {
      setState("idle");
    },
  });

  const clearInactivityTimer = useCallback(() => {
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
      inactivityTimeoutRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    if (countdownStartTimeoutRef.current) {
      clearTimeout(countdownStartTimeoutRef.current);
      countdownStartTimeoutRef.current = null;
    }
    setCountdown(null);
  }, []);

  const handleStartConversation = useCallback(async () => {
    try {
      setState("loading");
      conversationStartTime.current = Date.now();
      trackConversationStarted();

      // Get token - this also dispatches the agent job with widgetId
      const tokenResponse = await apiClientRef.current.getLiveKitToken(
        avatarId,
        voiceId
      );

      // Initialize avatar manager - this connects to room and publishes mic
      await avatarManager.initialize(tokenResponse);
    } catch (error) {
      console.error("Failed to start conversation:", error);
      trackError("connection", "start_conversation", String(error));
      setState("error");
    }
  }, [avatarManager, setState, avatarId, voiceId]);

  const handleStopConversation = useCallback(
    async (reason: "user_stopped" | "timeout" | "error" = "user_stopped") => {
      try {
        setState("loading");

        const duration = conversationStartTime.current
          ? Date.now() - conversationStartTime.current
          : 0;
        trackConversationEnded(duration, 0, reason);

        clearInactivityTimer();
        await avatarManager.destroy();
        conversationStartTime.current = null;
        setState("idle");
      } catch (error) {
        console.error("Failed to stop conversation:", error);
        trackError("cleanup", "stop_conversation", String(error));
        setState("error");
      }
    },
    [avatarManager, setState, clearInactivityTimer]
  );

  // Inactivity timer
  const INACTIVITY_TIMEOUT = 60_000; // 60 seconds (longer since agent handles VAD)
  const COUNTDOWN_START = 5000;

  const startInactivityTimer = useCallback(() => {
    clearInactivityTimer();

    const countdownStart = INACTIVITY_TIMEOUT - COUNTDOWN_START;
    countdownStartTimeoutRef.current = setTimeout(() => {
      let secondsLeft = 5;
      setCountdown(secondsLeft);

      countdownIntervalRef.current = setInterval(() => {
        secondsLeft--;
        if (secondsLeft > 0) {
          setCountdown(secondsLeft);
        } else {
          clearInterval(countdownIntervalRef.current!);
          countdownIntervalRef.current = null;
        }
      }, 1000);
    }, countdownStart);

    inactivityTimeoutRef.current = setTimeout(() => {
      clearInactivityTimer();
      void handleStopConversation("timeout");
    }, INACTIVITY_TIMEOUT);
  }, [clearInactivityTimer, handleStopConversation]);

  // Reset timer when state changes to listening
  useEffect(() => {
    if (state === "listening") {
      startInactivityTimer();
    } else {
      clearInactivityTimer();
    }

    return () => {
      clearInactivityTimer();
    };
  }, [state, startInactivityTimer, clearInactivityTimer]);

  const renderContent = () => {
    switch (state) {
      case "idle":
        return (
          <div
            className="overlay"
            onClick={handleStartConversation}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                handleStartConversation();
              }
            }}
            role="button"
            style={{ cursor: "pointer" }}
            tabIndex={0}
            title="Click to start conversation"
          >
            {placeholderImage && (
              <img
                alt="Avatar placeholder"
                className="placeholder-image"
                src={placeholderImage}
              />
            )}
            <div className="overlay-content">
              <div className="start-button" style={{ pointerEvents: "none" }}>
                <svg
                  fill="currentColor"
                  height="32"
                  viewBox="0 0 24 24"
                  width="32"
                >
                  <path d="M8 5v14l11-7z" />
                </svg>
              </div>
            </div>
          </div>
        );

      case "loading":
        return (
          <div className="overlay">
            <div className="overlay-content">
              <div className="loading-spinner" />
              <div className="loading-text">Connecting to avatar...</div>
            </div>
          </div>
        );

      case "active":
      case "listening":
      case "speaking":
      case "processing":
        return (
          <>
            <div className="status-indicator">
              <div
                className={`status-dot ${
                  state === "listening"
                    ? "listening"
                    : state === "speaking"
                      ? "speaking"
                      : state === "processing"
                        ? "processing"
                        : ""
                }`}
              />
              <span>
                {state === "listening"
                  ? "Listening"
                  : state === "speaking"
                    ? "Speaking"
                    : state === "processing"
                      ? "Thinking"
                      : "Connected"}
              </span>
            </div>
            <div className="controls">
              <button
                className={`control-button ${state === "listening" ? "active" : ""}`}
                onClick={() => handleStopConversation("user_stopped")}
                title="Stop conversation"
                type="button"
              >
                <svg
                  fill="currentColor"
                  height="24"
                  viewBox="0 0 24 24"
                  width="24"
                >
                  <path d="M6 6h12v12H6z" />
                </svg>
              </button>
            </div>
            {countdown !== null && (
              <div className="countdown-badge">Stopping in {countdown}s...</div>
            )}
          </>
        );

      case "error":
        return (
          <div className="overlay">
            <div className="overlay-content error-container">
              <div className="error-icon">!</div>
              <div className="error-message">
                Failed to connect to avatar. Please check your connection and
                try again.
              </div>
              <button
                className="retry-button"
                onClick={handleStartConversation}
                type="button"
              >
                Retry
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const containerStyle: React.CSSProperties = {
    width,
    height,
  };

  return (
    <div className={`widget-container ${position}`} style={containerStyle}>
      <video
        autoPlay
        className="video-element"
        muted={false}
        playsInline
        ref={videoRef}
      />
      {renderContent()}
    </div>
  );
}
